import { HttpHeaders } from '@angular/common/http';
import {
  getActiveUserDataRaw,
  removeImpersonationStorageItem,
  setImpersonationStorageItem,
  IMPERSONATION_USER_DATA_KEY,
} from '../../shared/utils/impersonation-handoff.util';

/**
 * Active shop JWT for THIS tab (sessionStorage impersonation, else shared userData).
 */
export function getActiveUserAuthToken(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const userData = getActiveUserDataRaw();
  if (!userData) {
    return undefined;
  }
  try {
    const token = JSON.parse(userData)?.token;
    return typeof token === 'string' && token ? token : undefined;
  } catch (error) {
    console.error('Invalid JSON in active user session:', error);
    removeImpersonationStorageItem(IMPERSONATION_USER_DATA_KEY);
    localStorage.removeItem('userData');
    return undefined;
  }
}

/** Super-admin JWT from `superAdminData` (not shop/impersonation storage). */
export function getSuperAdminAuthToken(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const raw = localStorage.getItem('superAdminData');
    if (!raw) {
      return undefined;
    }
    const token = JSON.parse(raw)?.token;
    return typeof token === 'string' && token ? token : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Build Authorization header from active shop session (tab-scoped impersonation preferred).
 * @param options.includeJsonContentType - If true, sets Content-Type to application/json
 */
export function getAuthHeadersFromUserData(options?: {
  includeJsonContentType?: boolean;
}): HttpHeaders {
  let headers = new HttpHeaders();
  if (options?.includeJsonContentType) {
    headers = headers.set('Content-Type', 'application/json');
  }
  const token = getActiveUserAuthToken();
  if (token) {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

function readActiveUserDataObject(): Record<string, any> | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = getActiveUserDataRaw();
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeActiveUserDataObject(userData: Record<string, any>): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (userData['impersonated']) {
    setImpersonationStorageItem(IMPERSONATION_USER_DATA_KEY, JSON.stringify(userData));
    return;
  }
  removeImpersonationStorageItem(IMPERSONATION_USER_DATA_KEY);
  localStorage.setItem('userData', JSON.stringify(userData));
}

export function getUserDataLocationId(): string | undefined {
  const location = readActiveUserDataObject()?.['location'];
  const locationId = location != null ? String(location).trim() : '';
  return locationId || undefined;
}

export function getUserDataLaneId(): string | undefined {
  const laneId = readActiveUserDataObject()?.['lane_id'];
  const normalized = laneId != null ? String(laneId).trim() : '';
  return normalized || undefined;
}

export function getUserDataLaneCameraRoi(): number | undefined {
  const raw = readActiveUserDataObject()?.['lane_camera_roi'];
  // A cleared lane stores `null`; Number(null) is 0, which would read as a selected ROI and
  // filter out every other lane's notifications (CWS-1870).
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return undefined;
  }
  const cameraRoi = Number(raw);
  return Number.isInteger(cameraRoi) ? cameraRoi : undefined;
}

/**
 * Persist the active location id into the active shop session storage.
 * Used when a remote selected-lane change (from another session) came from a DIFFERENT location,
 * so this session realigns its location before resolving/loading the pushed lane.
 */
export function setUserDataLocationId(locationId?: string | null): void {
  const userData = readActiveUserDataObject();
  if (!userData) {
    return;
  }
  const normalized =
    locationId != null && String(locationId).trim() !== '' ? String(locationId).trim() : null;
  userData['location'] = normalized;
  writeActiveUserDataObject(userData);
}

export function setUserDataLaneId(laneId?: string, cameraRoi?: number | null): void {
  const userData = readActiveUserDataObject();
  if (!userData) {
    return;
  }
  userData['lane_id'] = laneId ?? null;
  if (cameraRoi === undefined) {
    // keep existing camera roi when caller only updates lane id
  } else if (cameraRoi == null || !Number.isInteger(Number(cameraRoi))) {
    userData['lane_camera_roi'] = null;
  } else {
    userData['lane_camera_roi'] = Number(cameraRoi);
  }
  writeActiveUserDataObject(userData);
}
